import React, { useEffect, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  FormControl,
  Select,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import {
  useGetClientQuery,
  useCreateClientMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "../../../Components/Pagination";
import useIsMobile from "../../../Core/hook/useIsMobile";

interface IClient {
  id: string;
  preferredName: string;
  firstName: string;
  lastName: string;
  businessUnit: string;
  status: string;
  diagnosis?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
}

const DIAGNOSIS_OPTIONS = [
  "Autism Spectrum Disorder",
  "ADHD",
  "Down Syndrome",
  "Intellectual Disability",
  "Speech/Language Delay",
  "Other",
];

const STATUS_OPTIONS = ["Active", "Inactive", "Pending"];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const isMobileRes = useIsMobile();

  // Search & pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);

  // Dialog
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);

  // API hooks
  const { data, isLoading, isError, refetch } = useGetClientQuery({
    searchText: currentSearchQuery,
    status: statusFilter !== "all" ? statusFilter : undefined,
  });
  const [createClient, { isLoading: creating }] = useCreateClientMutation();

  useEffect(() => {
    if (debouncedSearchQuery.length >= 3 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const clients: IClient[] = Array.isArray(data?.data) ? (data?.data as IClient[]) : [];

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Add Client form
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      preferredName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      businessUnit: "",
      diagnosis: "",
      status: "Active",
      notes: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await createClient(values).unwrap();
        if (response.status) {
          showMessage(response.message || "Client added successfully");
          setAddClientDialogOpen(false);
          resetForm();
          setDialogTab(0);
          refetch();
        } else {
          showErrorMessage(response.message || "Failed to add client");
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, client: IClient) => {
    setMenuAnchor(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedClient(null);
  };

  const getStatusColor = (status: string): "success" | "error" | "default" | "warning" => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Card className={classes.card}>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        spacing={2}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={4} md={4} sm={12} xs={12}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch className={classes.searchWidgetIcon} />
                </InputAdornment>
              ),
              classes: { root: classes.formControlList },
            }}
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clients..."
            className={classes.searchTextField}
            size="small"
          />
        </Grid>
        <Grid
          item
          lg={8}
          md={8}
          sm={12}
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, alignItems: "center" }}
        >
          {/* Filter pills */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Chip
              label="Active"
              size="small"
              variant={statusFilter === "Active" ? "filled" : "outlined"}
              color={statusFilter === "Active" ? "success" : "default"}
              onClick={() => setStatusFilter(statusFilter === "Active" ? "all" : "Active")}
            />
            <Chip
              label="Inactive"
              size="small"
              variant={statusFilter === "Inactive" ? "filled" : "outlined"}
              color={statusFilter === "Inactive" ? "error" : "default"}
              onClick={() => setStatusFilter(statusFilter === "Inactive" ? "all" : "Inactive")}
            />
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Box>

          <IconButton size="small">
            <FileDownloadIcon fontSize="small" />
          </IconButton>

          <Button
            className={classes.addNewButton}
            startIcon={<PersonAddIcon />}
            onClick={() => setAddClientDialogOpen(true)}
          >
            Add Client
          </Button>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer>
        <Table className={classes.table}>
          <TableHead className={classes.tableHeaderRow}>
            <TableRow>
              <TableCell className={classes.tablheader}>
                <strong>Preferred Name</strong>
              </TableCell>
              {!isMobileRes && (
                <TableCell className={classes.tablheader}>
                  <strong>Business Unit</strong>
                </TableCell>
              )}
              <TableCell className={classes.tablheader}>
                <strong>Status</strong>
              </TableCell>
              <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                </TableCell>
              </TableRow>
            ) : isError || clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {isError ? "Error fetching clients" : "No clients found"}
                </TableCell>
              </TableRow>
            ) : (
              clients
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((client) => (
                  <TableRow key={client.id} className={classes.tableRowHover}>
                    <TableCell
                      className={classes.tablCell}
                      sx={{ cursor: "pointer", color: "primary.main" }}
                      onClick={() => navigate(`/student/view/${client.id}`)}
                    >
                      {client.preferredName || `${client.firstName} ${client.lastName}`}
                    </TableCell>
                    {!isMobileRes && (
                      <TableCell className={classes.tablCell}>{client.businessUnit}</TableCell>
                    )}
                    <TableCell className={classes.tablCell}>
                      <Chip
                        label={client.status}
                        size="small"
                        color={getStatusColor(client.status)}
                      />
                    </TableCell>
                    <TableCell className={classes.tablCell}>
                      <Grid container spacing={1} justifyContent="flex-end">
                        <Grid item>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/client/edit/${client.id}`)}
                          >
                            <EditSharpIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/student/view/${client.id}`)}
                          >
                            <GroupIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, client)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <Grid spacing={5} p="18px" py={4} justifyContent="end" container>
          <Grid sx={{ textAlign: "end", justifyContent: "flex-end" }}>
            <Pagination
              count={clients.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Grid>
        </Grid>
      </TableContainer>

      {/* Three-dot menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedClient) navigate(`/student/view/${selectedClient.id}`);
            handleMenuClose();
          }}
        >
          View Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedClient) navigate(`/client/edit/${selectedClient.id}`);
            handleMenuClose();
          }}
        >
          Edit Client
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>Deactivate</MenuItem>
      </Menu>

      {/* Add Client Dialog */}
      <Dialog
        open={addClientDialogOpen}
        onClose={() => {
          setAddClientDialogOpen(false);
          formik.resetForm();
          setDialogTab(0);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Client</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={dialogTab} onChange={(_e, v) => setDialogTab(v)}>
              <Tab label="Profile" />
              <Tab label="Client Information" />
              <Tab label="Access" />
            </Tabs>
          </Box>

          {/* Tab 0 - Profile */}
          <TabPanel value={dialogTab} index={0}>
            <Grid container spacing={3}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">
                  First Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  placeholder="First Name"
                  size="small"
                  fullWidth
                  {...formik.getFieldProps("firstName")}
                  className={classes.textField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">
                  Last Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  placeholder="Last Name"
                  size="small"
                  fullWidth
                  {...formik.getFieldProps("lastName")}
                  className={classes.textField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Preferred Name</Typography>
                <TextField
                  placeholder="Preferred Name"
                  size="small"
                  fullWidth
                  {...formik.getFieldProps("preferredName")}
                  className={classes.textField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Date of Birth</Typography>
                <AppDatePicker
                  size="small"
                  fullWidth
                  value={formik.values.dateOfBirth || ""}
                  onChange={(v) => formik.setFieldValue("dateOfBirth", v)}
                  sx={{ fontSize: 14 }}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Email</Typography>
                <TextField
                  placeholder="Email address"
                  size="small"
                  fullWidth
                  {...formik.getFieldProps("email")}
                  className={classes.textField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Phone</Typography>
                <TextField
                  placeholder="Phone number"
                  size="small"
                  fullWidth
                  {...formik.getFieldProps("phone")}
                  className={classes.textField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1 - Client Information */}
          <TabPanel value={dialogTab} index={1}>
            <Grid container spacing={3}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Business Unit</Typography>
                <TextField
                  placeholder="Business Unit"
                  size="small"
                  fullWidth
                  {...formik.getFieldProps("businessUnit")}
                  className={classes.textField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Diagnosis</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    {...formik.getFieldProps("diagnosis")}
                    displayEmpty
                    className={classes.textField}
                  >
                    <MenuItem value="">Select Diagnosis</MenuItem>
                    {DIAGNOSIS_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Typography variant="subtitle1">Status</Typography>
                <FormControl fullWidth size="small">
                  <Select {...formik.getFieldProps("status")} className={classes.textField}>
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography variant="subtitle1">Notes</Typography>
                <TextField
                  placeholder="Additional notes..."
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  {...formik.getFieldProps("notes")}
                  className={classes.discriptionTextField}
                  InputProps={{ sx: { fontSize: 14 } }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2 - Access */}
          <TabPanel value={dialogTab} index={2}>
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography variant="body1" color="text.secondary" sx={{ py: 4 }} align="center">
                  Access settings will be configured after the client is created. You can assign
                  team members and set permissions from the client profile page.
                </Typography>
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddClientDialogOpen(false);
              formik.resetForm();
              setDialogTab(0);
            }}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={!formik.values.firstName.trim() || !formik.values.lastName.trim() || creating}
            startIcon={creating ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            Save Client
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default Index;
